import { randomBytes } from "node:crypto";
import fs from "node:fs/promises";
import path from "node:path";
import { ChannelManager } from "@mewbleh/nori-channels";
import { AgentRuntime, loadConfig, PluginManager, ProviderRegistry, resolveSecret, type NoriConfig } from "@mewbleh/nori-core";
import {
  AgentStore,
  agentDatabasePath,
  ensureNoriDirectories,
  resolveNoriPaths,
  SecretVault,
  SharedStore,
  type NoriPaths,
} from "@mewbleh/nori-persistence";

export type AppContext = {
  paths: NoriPaths;
  config: NoriConfig;
  vault: SecretVault;
  agentStore: AgentStore;
  agentStores: Map<string, AgentStore>;
  sharedStore: SharedStore;
  providers: ProviderRegistry;
  runtime: AgentRuntime;
  channels: ChannelManager;
  pluginManager: PluginManager;
  loadedPlugins: string[];
  storeForAgent(agentId: string): AgentStore;
  close(): Promise<void>;
};

export async function createContext(profile?: string, options: { loadPlugins?: boolean; initializeMcp?: boolean } = {}): Promise<AppContext> {
  const paths = resolveNoriPaths({ ...(profile ? { profile } : {}) });
  await ensureNoriDirectories(paths);
  const config = await loadConfig(paths);
  const vault = new SecretVault(paths.masterKeyPath);
  await vault.initialize();
  const agentStore = new AgentStore(agentDatabasePath(paths, "main"), vault);
  const sharedStore = new SharedStore(paths.sharedDatabasePath);
  const providers = new ProviderRegistry();
  const agentStores = new Map<string, AgentStore>([["main", agentStore]]);
  const storeForAgent = (agentId: string): AgentStore => {
    const existing = agentStores.get(agentId);
    if (existing) return existing;
    const store = new AgentStore(agentDatabasePath(paths, agentId), vault);
    agentStores.set(agentId, store);
    return store;
  };
  for (const agent of config.agents.list) storeForAgent(agent.id);
  const runtime = new AgentRuntime(config, agentStore, providers, process.env, sharedStore, storeForAgent);
  if (options.initializeMcp !== false) await runtime.initializeMcp();
  const channels = new ChannelManager(config, paths, runtime, sharedStore);
  const pluginManager = new PluginManager(paths, sharedStore, providers, channels);
  const loadedPlugins = options.loadPlugins === false ? [] : await pluginManager.loadEnabled(config.plugins.allow);
  return {
    paths,
    config,
    vault,
    agentStore,
    agentStores,
    sharedStore,
    providers,
    runtime,
    channels,
    pluginManager,
    loadedPlugins,
    storeForAgent,
    close: async () => {
      await channels.stop();
      runtime.close();
      for (const store of agentStores.values()) store.close();
      sharedStore.close();
    },
  };
}

export async function resolveGatewayToken(context: Pick<AppContext, "config" | "agentStore">): Promise<string> {
  const envToken = process.env.NORI_GATEWAY_TOKEN ?? process.env.OPENCLAW_GATEWAY_TOKEN;
  const token = envToken ?? (await resolveSecret(context.config.gateway.auth.token)) ?? context.agentStore.getCredential("gateway");
  if (!token) throw new Error("Gateway token is not configured. Run `nori setup`.");
  return token;
}

export async function ensureGatewayToken(paths: NoriPaths, config: NoriConfig): Promise<string> {
  const tokenPath = path.join(paths.credentialsDir, "gateway.token");
  let token: string;
  try {
    token = (await fs.readFile(tokenPath, "utf8")).trim();
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    token = randomBytes(32).toString("base64url");
    await fs.writeFile(tokenPath, `${token}\n`, { flag: "wx", mode: 0o600 });
  }
  config.gateway.auth.token = { source: "file", path: tokenPath };
  return token;
}

export function gatewayUrl(config: NoriConfig): string {
  const host = config.gateway.bind === "0.0.0.0" ? "127.0.0.1" : config.gateway.bind;
  return `ws://${host.includes(":") && !host.startsWith("[") ? `[${host}]` : host}:${config.gateway.port}`;
}
